import { LuPause, LuPlay, LuPlus, LuTrash2, LuVideo, LuX, LuCalendar, LuClock } from 'react-icons/lu';
import { Link, useNavigate } from 'react-router-dom';
import { SITELINK } from '../../app-routes/Links';
import { useUserCommunities } from '../../api-services/community/useUserCommunities';
import { Option } from 'react-multi-select-component';
import feeds_api from '../../api-services/feeds/feeds.services';
import { useEffect, useState, useRef } from 'react';
import { LuImage, LuMic } from 'react-icons/lu';
import { Community } from '../../app-schemas/community/community.interface';
import { getFirstLetters, getUser, tinyAlert, truncateText } from '../../app-utils';
import { UserInterface } from '../../app-schemas/profile.schema';
import { Verify } from '../../assets/custom-icons';
import Modal from '../../components/modal';
import { ComposerBanner } from '../../assets/images';
import TopBar from '../../layouts/~includes/TopBar';

// Salary frequency options
const salaryFrequencyOptions = [
  { value: 'day', label: 'Per Day' },
  { value: 'week', label: 'Per Week' },
  { value: 'month', label: 'Per Month' },
  { value: 'year', label: 'Per Year' },
];

// User type options for rides
const userTypeOptions = [
  { value: 'driver', label: 'Driver' },
  { value: 'passenger', label: 'Passenger' },
];

// Task type options
const taskTypeOptions = [
  { value: 'remote', label: 'Remote' },
  { value: 'onsite', label: 'On-site' },
  { value: 'hybrid', label: 'Hybrid' },
];

// ─── Image Compression Utility ───────────────────────────────────────────────
const compressImage = (file: File, maxWidth = 756, quality = 0.60): Promise<File> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(file);
            return;
          }
          const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), {
            type: 'image/webp',
            lastModified: Date.now(),
          });
          resolve(compressed);
        },
        'image/webp',
        quality,
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };

    img.src = objectUrl;
  });
};

// Calendar Picker Component
interface CalendarPickerProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  position: { top: number; left: number; width: number };
}

const CalendarPicker = ({ value, onChange, onClose, position }: CalendarPickerProps) => {
  const [currentDate, setCurrentDate] = useState(new Date(value || new Date()));
  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarPosition, setCalendarPosition] = useState<'top' | 'bottom'>('bottom');

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();

  const days = [];
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const handleDateSelect = (day: number) => {
    const selectedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const formattedDate = selectedDate.toISOString().split('T')[0];
    onChange(formattedDate);
    onClose();
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    if (!value) return false;
    const selectedDate = new Date(value);
    return (
      day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  useEffect(() => {
    if (calendarRef.current) {
      const calendarHeight = 320;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - (position.top + 50);
      const spaceAbove = position.top - 50;

      if (spaceBelow < calendarHeight && spaceAbove > spaceBelow) {
        setCalendarPosition('top');
      } else {
        setCalendarPosition('bottom');
      }
    }
  }, [position.top]);

  const calendarStyle = {
    position: 'fixed' as const,
    left: `${Math.min(position.left, window.innerWidth - 320)}px`,
    ...(calendarPosition === 'bottom'
      ? { top: `${position.top + 50}px` }
      : { bottom: `${window.innerHeight - position.top + 50}px` }),
    width: `${Math.min(position.width, 320)}px`,
    zIndex: 9999,
  };

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />
      <div
        ref={calendarRef}
        className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl border border-gray-200 dark:border-neutral-700 p-4"
        style={calendarStyle}
      >
        <div className="flex justify-between items-center mb-4">
          <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-full">
            <svg className="w-5 h-5 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="font-semibold dark:text-white">
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button onClick={handleNextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-700 rounded-full">
            <svg className="w-5 h-5 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDayOfMonth }).map((_, index) => (
            <div key={`empty-${index}`} className="h-8" />
          ))}
          {days.map((day) => (
            <button
              key={day}
              onClick={() => handleDateSelect(day)}
              className={`h-8 rounded-full flex items-center justify-center text-sm dark:text-white ${
                isToday(day) ? 'border border-blue-500' : ''
              } ${isSelected(day) ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-neutral-700'}`}
            >
              {day}
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

// Time Picker Component
interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  position: { top: number; left: number; width: number };
}

const TimePicker = ({ value, onChange, onClose, position }: TimePickerProps) => {
  const [selectedHour, setSelectedHour] = useState(value ? parseInt(value.split(':')[0]) : 12);
  const [selectedMinute, setSelectedMinute] = useState(value ? parseInt(value.split(':')[1]) : 0);
  const [isAM, setIsAM] = useState(value ? parseInt(value.split(':')[0]) < 12 : true);
  const timePickerRef = useRef<HTMLDivElement>(null);
  const [pickerPosition, setPickerPosition] = useState<'top' | 'bottom'>('bottom');

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  const handleTimeSelect = () => {
    const hour24 = isAM
      ? selectedHour === 12
        ? 0
        : selectedHour
      : selectedHour === 12
      ? 12
      : selectedHour + 12;
    const formattedTime = `${hour24.toString().padStart(2, '0')}:${selectedMinute
      .toString()
      .padStart(2, '0')}`;
    onChange(formattedTime);
    onClose();
  };

  useEffect(() => {
    if (timePickerRef.current) {
      const pickerHeight = 280;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - (position.top + 50);
      const spaceAbove = position.top - 50;

      if (spaceBelow < pickerHeight && spaceAbove > spaceBelow) {
        setPickerPosition('top');
      } else {
        setPickerPosition('bottom');
      }
    }
  }, [position.top]);

  const pickerStyle = {
    position: 'fixed' as const,
    left: `${Math.min(position.left, window.innerWidth - 280)}px`,
    ...(pickerPosition === 'bottom'
      ? { top: `${position.top + 50}px` }
      : { bottom: `${window.innerHeight - position.top + 50}px` }),
    width: `${Math.min(position.width, 280)}px`,
    zIndex: 9999,
  };

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />
      <div
        ref={timePickerRef}
        className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl border border-gray-200 dark:border-neutral-700 p-4"
        style={pickerStyle}
      >
        <div className="flex justify-between mb-4">
          <div className="flex-1">
            <div className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Hour</div>
            <div className="h-40 overflow-y-auto">
              {hours.map((hour) => (
                <button
                  key={hour}
                  onClick={() => setSelectedHour(hour)}
                  className={`w-full py-2 text-center rounded dark:text-white ${
                    selectedHour === hour ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-neutral-700'
                  }`}
                >
                  {hour.toString().padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1">
            <div className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Minute</div>
            <div className="h-40 overflow-y-auto">
              {minutes.map((minute) => (
                <button
                  key={minute}
                  onClick={() => setSelectedMinute(minute)}
                  className={`w-full py-2 text-center rounded dark:text-white ${
                    selectedMinute === minute ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-neutral-700'
                  }`}
                >
                  {minute.toString().padStart(2, '0')}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1">
            <div className="text-center text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">AM/PM</div>
            <div className="h-40">
              <button
                onClick={() => setIsAM(true)}
                className={`w-full py-8 text-center rounded mb-2 dark:text-white ${
                  isAM ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-neutral-700'
                }`}
              >
                AM
              </button>
              <button
                onClick={() => setIsAM(false)}
                className={`w-full py-8 text-center rounded dark:text-white ${
                  !isAM ? 'bg-blue-500 text-white' : 'hover:bg-gray-100 dark:hover:bg-neutral-700'
                }`}
              >
                PM
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-between mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 dark:border-neutral-600 dark:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleTimeSelect}
            className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            OK
          </button>
        </div>
      </div>
    </>
  );
};

// DateTime Picker Component
interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  position: { top: number; left: number; width: number };
}

const DateTimePicker = ({ value, onChange, onClose, position }: DateTimePickerProps) => {
  const [selectedDate, setSelectedDate] = useState(value ? value.split('T')[0] : '');
  const [selectedTime, setSelectedTime] = useState(value ? value.split('T')[1] : '12:00');
  const [mode, setMode] = useState<'date' | 'time'>('date');
  const pickerRef = useRef<HTMLDivElement>(null);
  const [pickerPosition, setPickerPosition] = useState<'top' | 'bottom'>('bottom');

  useEffect(() => {
    if (pickerRef.current) {
      const pickerHeight = mode === 'date' ? 320 : 280;
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - (position.top + 50);
      const spaceAbove = position.top - 50;

      if (spaceBelow < pickerHeight && spaceAbove > spaceBelow) {
        setPickerPosition('top');
      } else {
        setPickerPosition('bottom');
      }
    }
  }, [position.top, mode]);

  const handleDateTimeSelect = () => {
    onChange(`${selectedDate}T${selectedTime}`);
    onClose();
  };

  const pickerStyle = {
    position: 'fixed' as const,
    left: `${Math.min(position.left, window.innerWidth - 320)}px`,
    ...(pickerPosition === 'bottom'
      ? { top: `${position.top + 50}px` }
      : { bottom: `${window.innerHeight - position.top + 50}px` }),
    width: `${Math.min(position.width, 320)}px`,
    zIndex: 9999,
  };

  return (
    <>
      <div className="fixed inset-0 z-50" onClick={onClose} />
      <div
        ref={pickerRef}
        className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl border border-gray-200 dark:border-neutral-700 p-4"
        style={pickerStyle}
      >
        <div className="flex border-b dark:border-neutral-700 mb-4">
          <button
            onClick={() => setMode('date')}
            className={`flex-1 py-2 text-center dark:text-white ${
              mode === 'date' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Date
          </button>
          <button
            onClick={() => setMode('time')}
            className={`flex-1 py-2 text-center dark:text-white ${
              mode === 'time' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            Time
          </button>
        </div>

        {mode === 'date' ? (
          <div>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full p-2 border rounded mb-4 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Or select from calendar</p>
            <div className="h-40 overflow-y-auto">
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <button
                    key={day}
                    onClick={() => {
                      const date = new Date();
                      date.setDate(day);
                      setSelectedDate(date.toISOString().split('T')[0]);
                    }}
                    className="p-2 text-center hover:bg-gray-100 dark:hover:bg-neutral-700 rounded dark:text-white"
                  >
                    {day}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div>
            <input
              type="time"
              value={selectedTime}
              onChange={(e) => setSelectedTime(e.target.value)}
              className="w-full p-2 border rounded mb-4 dark:bg-neutral-700 dark:border-neutral-600 dark:text-white"
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Or select from time picker</p>
            <div className="h-40 overflow-y-auto">
              {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                <button
                  key={hour}
                  onClick={() => setSelectedTime(`${hour.toString().padStart(2, '0')}:00`)}
                  className="w-full p-2 text-center hover:bg-gray-100 dark:hover:bg-neutral-700 rounded dark:text-white"
                >
                  {hour.toString().padStart(2, '00')}:00
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 dark:border-neutral-600 dark:text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleDateTimeSelect}
            className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            OK
          </button>
        </div>
      </div>
    </>
  );
};

// Custom Date Input Component
const CustomDateInput = ({
  value,
  onChange,
  placeholder,
  error,
  className = '',
  ...props
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  error?: string;
  className?: string;
} & React.InputHTMLAttributes<HTMLInputElement>) => {
  const [displayValue, setDisplayValue] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        setDisplayValue(
          date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
        );
      } else {
        setDisplayValue('');
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleDateSelect = (dateValue: string) => {
    onChange({ target: { value: dateValue } } as React.ChangeEvent<HTMLInputElement>);
  };

  const getPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      return { top: rect.top, left: rect.left, width: rect.width };
    }
    return { top: 0, left: 0, width: 0 };
  };

  return (
    <div className="relative">
      <div
        ref={inputRef}
        onClick={() => setShowCalendar(true)}
        className={`w-full rounded-md px-4 py-3 text-sm border cursor-pointer flex items-center justify-between ${
          error ? 'border-red-500' : 'focus:border-custom-500'
        } ${className} ${!value ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'} dark:bg-neutral-800 dark:border-neutral-700`}
      >
        <span>{value ? displayValue : placeholder}</span>
        <LuCalendar className="w-5 h-5 text-gray-400 dark:text-gray-500" />
      </div>
      <input
        type="date"
        value={value}
        onChange={onChange}
        className="absolute inset-0 w-full h-full opacity-0"
        {...props}
      />
      {showCalendar && (
        <CalendarPicker
          value={value}
          onChange={handleDateSelect}
          onClose={() => setShowCalendar(false)}
          position={getPosition()}
        />
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

// Custom Time Input Component
const CustomTimeInput = ({
  value,
  onChange,
  placeholder,
  error,
  className = '',
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  error?: string;
  className?: string;
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [showTimePicker, setShowTimePicker] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const [hours, minutes] = value.split(':');
      const hour = parseInt(hours, 10);
      const minute = minutes ? parseInt(minutes, 10) : 0;
      const period = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      setDisplayValue(`${displayHour}:${minute.toString().padStart(2, '0')} ${period}`);
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleTimeSelect = (timeValue: string) => {
    onChange({ target: { value: timeValue } } as React.ChangeEvent<HTMLInputElement>);
  };

  const getPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      return { top: rect.top, left: rect.left, width: rect.width };
    }
    return { top: 0, left: 0, width: 0 };
  };

  return (
    <div className="relative">
      <div
        ref={inputRef}
        onClick={() => setShowTimePicker(true)}
        className={`w-full rounded-md px-4 py-3 text-sm border cursor-pointer flex items-center justify-between ${
          error ? 'border-red-500' : 'focus:border-custom-500'
        } ${className} ${!value ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'} dark:bg-neutral-800 dark:border-neutral-700`}
      >
        <span>{value ? displayValue : placeholder}</span>
        <LuClock className="w-5 h-5 text-gray-400 dark:text-gray-500" />
      </div>
      <input
        type="time"
        value={value}
        onChange={onChange}
        className="absolute inset-0 w-full h-full opacity-0"
      />
      {showTimePicker && (
        <TimePicker
          value={value}
          onChange={handleTimeSelect}
          onClose={() => setShowTimePicker(false)}
          position={getPosition()}
        />
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

// Custom DateTime Input Component
const CustomDateTimeInput = ({
  value,
  onChange,
  placeholder,
  error,
  className = '',
}: {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  error?: string;
  className?: string;
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const dateStr = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
        const timeStr = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        setDisplayValue(`${dateStr} ${timeStr}`);
      } else {
        setDisplayValue('');
      }
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleDateTimeSelect = (dateTimeValue: string) => {
    onChange({ target: { value: dateTimeValue } } as React.ChangeEvent<HTMLInputElement>);
  };

  const getPosition = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      return { top: rect.top, left: rect.left, width: rect.width };
    }
    return { top: 0, left: 0, width: 0 };
  };

  return (
    <div className="relative">
      <div
        ref={inputRef}
        onClick={() => setShowDateTimePicker(true)}
        className={`w-full rounded-md px-4 py-3 text-sm border cursor-pointer flex items-center justify-between ${
          error ? 'border-red-500' : 'focus:border-custom-500'
        } ${className} ${!value ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'} dark:bg-neutral-800 dark:border-neutral-700`}
      >
        <span>{value ? displayValue : placeholder}</span>
        <LuCalendar className="w-5 h-5 text-gray-400 dark:text-gray-500" />
      </div>
      <input
        type="datetime-local"
        value={value}
        onChange={onChange}
        className="absolute inset-0 w-full h-full opacity-0"
      />
      {showDateTimePicker && (
        <DateTimePicker
          value={value}
          onChange={handleDateTimeSelect}
          onClose={() => setShowDateTimePicker(false)}
          position={getPosition()}
        />
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

// Custom Dropdown Component
interface DropdownOption {
  value: string;
  label: string;
}

const CustomDropdown = ({
  options,
  value,
  onChange,
  placeholder,
  error,
  className = '',
}: {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  error?: string;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const dropdownHeight = options.length * 40 + 50;
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen, options.length]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        className={`w-full rounded-md px-4 py-3 text-sm border text-left flex justify-between items-center ${
          error ? 'border-red-500' : 'focus:border-custom-500'
        } ${className} ${!selectedOption ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'} dark:bg-neutral-800 dark:border-neutral-700`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{selectedOption?.label || placeholder}</span>
        <svg
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'transform rotate-180' : ''} dark:text-white`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 w-full bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-md shadow-lg max-h-60 overflow-y-auto ${
            dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
          }`}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-neutral-700 text-sm ${
                value === option.value ? 'bg-custom-50 dark:bg-custom-900/30 text-custom-600 dark:text-custom-400' : 'text-gray-700 dark:text-gray-300'
              }`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

// Loading Percentage Component
const LoadingPercentage = ({ percentage }: { percentage: number }) => {
  return (
    <div className="flex items-center justify-center gap-2">
      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      <span>{Math.round(percentage)}%</span>
    </div>
  );
};

// Main component UI
const Composer = () => {
  document.body.classList.remove('overflow-hidden');
  const user: UserInterface = getUser();
  const navigate = useNavigate();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [postType, setPostType] = useState<
    'tweet' | 'job' | 'market' | 'events' | 'poll' | 'task' | 'rent' | 'rides'
  >('tweet');

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [userCommunitiesOptions, setUserCommunitiesOptions] = useState<Option[]>([]);
  const [communityID, setCommunityID] = useState<Option[]>([]);
  const [selectedCommunities, setSelectedCommunities] = useState<string[]>([]);
  const [textContent, setTextContent] = useState<string>('');
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudio] = useState<string | Blob>('');
  const [isPaused, setIsPaused] = useState(false);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const [multipleFile, setMultipleFile] = useState<File[] | null>(null);
  const [errmessage, setErrmessage] = useState('');
  const [errmessageII, setErrmessageII] = useState('');
  const [hashtag, setHashtag] = useState('');
  const [showTypeModal, setShowTypeModal] = useState(true);
  const [images, setImages] = useState<string[]>([]);

  console.log(communityID);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Job states
  const [jobTitle, setJobTitle] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [jobLocation, setJobLocation] = useState('');
  const [salary, setSalary] = useState('');
  const [salaryFrequency, setSalaryFrequency] = useState('');
  const [startDate, setStartDate] = useState('');
  const [deadline, setDeadline] = useState('');
  const [jobImages, setJobImages] = useState<File[]>([]);
  const [jobVideos, setJobVideos] = useState<File[]>([]);

  // Ride states
  const [userType, setUserType] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [tripDestination, setTripDestination] = useState('');
  const [offeredAmount, setOfferedAmount] = useState('');
  const [tripDate, setTripDate] = useState('');
  const [tripTime, setTripTime] = useState('');

  // Event states
  const [eventTitle, setEventTitle] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [eventLocation, setEventLocation] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [eventImages, setEventImages] = useState<File[]>([]);
  const [eventVideos, setEventVideos] = useState<File[]>([]);

  // Market states
  const [productTitle, setProductTitle] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productAmount, setProductAmount] = useState('');
  const [productImages, setProductImages] = useState<File[]>([]);

  // Task states
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskAmount, setTaskAmount] = useState('');
  const [taskStart, setTaskStart] = useState('');
  const [taskEnd, setTaskEnd] = useState('');
  const [taskType, setTaskType] = useState('');
  const [taskImages, setTaskImages] = useState<File[]>([]);

  // Poll states
  const [pollQuestion, setPollQuestion] = useState('');
  const [pollDescription, setPollDescription] = useState('');
  const [pollChoices, setPollChoices] = useState<string[]>(['', '']);
  const [newChoice, setNewChoice] = useState('');

  // Rent states
  const [apartmentTitle, setApartmentTitle] = useState('');
  const [apartmentDescription, setApartmentDescription] = useState('');
  const [apartmentRent, setApartmentRent] = useState('');
  const [apartmentAddress, setApartmentAddress] = useState('');
  const [apartmentImages, setApartmentImages] = useState<File[]>([]);
  const [apartmentVideos, setApartmentVideos] = useState<File[]>([]);

  const userCommunities = useUserCommunities();

  useEffect(() => {
    if (userCommunities.isSuccess) {
      const options = userCommunities?.data?.data?.data?.map((option: Community) => ({
        label: `${option.name}`,
        description: `${option.city}, ${option.country}`,
        value: option.id,
      }));
      setUserCommunitiesOptions(options);
      setCommunityID(options);
    }
  }, [userCommunities.isSuccess, userCommunities.data]);

  // ─── Reset helpers ────────────────────────────────────────────────────────────

  const resetJobForm = () => {
    setJobTitle('');
    setJobDescription('');
    setJobLocation('');
    setSalary('');
    setSalaryFrequency('');
    setStartDate('');
    setDeadline('');
    setJobImages([]);
    setJobVideos([]);
  };

  const resetRideForm = () => {
    setUserType('');
    setPickupLocation('');
    setTripDestination('');
    setOfferedAmount('');
    setTripDate('');
    setTripTime('');
  };

  const resetEventForm = () => {
    setEventTitle('');
    setEventDescription('');
    setEventLocation('');
    setEventStartTime('');
    setEventEndTime('');
    setEventImages([]);
    setEventVideos([]);
  };

  const resetMarketForm = () => {
    setProductTitle('');
    setProductDescription('');
    setProductAmount('');
    setProductImages([]);
  };

  const resetTaskForm = () => {
    setTaskTitle('');
    setTaskDescription('');
    setTaskAmount('');
    setTaskStart('');
    setTaskEnd('');
    setTaskType('');
    setTaskImages([]);
  };

  const resetPollForm = () => {
    setPollQuestion('');
    setPollDescription('');
    setPollChoices(['', '']);
    setNewChoice('');
  };

  const resetRentForm = () => {
    setApartmentTitle('');
    setApartmentDescription('');
    setApartmentRent('');
    setApartmentAddress('');
    setApartmentImages([]);
    setApartmentVideos([]);
  };

  const resetFormForHashtag = (tag: string) => {
    setErrors({});
    switch (tag) {
      case '#job':
        return resetJobForm();
      case '#rides':
        return resetRideForm();
      case '#events':
        return resetEventForm();
      case '#market':
        return resetMarketForm();
      case '#task':
        return resetTaskForm();
      case '#poll':
        return resetPollForm();
      case '#rent':
        return resetRentForm();
    }
  };

  const openCommunitiesModal = (type: typeof postType) => {
    setPostType(type);
    setShowTypeModal(false);
    setOpenModal(true);
    document.body.classList.add('overflow-hidden');
  };

  const handleDismissTypeModal = (tag: string) => {
    resetFormForHashtag(tag);
    setHashtag('');
    setShowTypeModal(true);
  };

  // Validation functions
  const validateNumber = (value: string, fieldName: string): boolean => {
    if (!value) {
      setErrors((prev) => ({ ...prev, [fieldName]: `${fieldName} is required` }));
      return false;
    }
    if (isNaN(Number(value)) || Number(value) <= 0) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: `${fieldName} must be a valid positive number`,
      }));
      return false;
    }
    setErrors((prev) => ({ ...prev, [fieldName]: '' }));
    return true;
  };

  const validateRequired = (value: string, fieldName: string): boolean => {
    if (!value.trim()) {
      setErrors((prev) => ({ ...prev, [fieldName]: `${fieldName} is required` }));
      return false;
    }
    setErrors((prev) => ({ ...prev, [fieldName]: '' }));
    return true;
  };

  const validateDate = (value: string, fieldName: string): boolean => {
    if (!value) {
      setErrors((prev) => ({ ...prev, [fieldName]: `${fieldName} is required` }));
      return false;
    }
    if (new Date(value) < new Date()) {
      setErrors((prev) => ({ ...prev, [fieldName]: `${fieldName} cannot be in the past` }));
      return false;
    }
    setErrors((prev) => ({ ...prev, [fieldName]: '' }));
    return true;
  };

  const validateJobForm = (): boolean => {
    const validations = [
      validateRequired(jobTitle, 'jobTitle'),
      validateRequired(jobDescription, 'jobDescription'),
      validateRequired(jobLocation, 'jobLocation'),
      validateNumber(salary, 'salary'),
      validateRequired(salaryFrequency, 'salaryFrequency'),
      validateDate(startDate, 'startDate'),
      validateDate(deadline, 'deadline'),
    ];
    return validations.every((v) => v);
  };

  const validateRideForm = (): boolean => {
    const validations = [
      validateRequired(userType, 'userType'),
      validateRequired(pickupLocation, 'pickupLocation'),
      validateRequired(tripDestination, 'tripDestination'),
      validateNumber(offeredAmount, 'offeredAmount'),
      validateDate(tripDate, 'tripDate'),
      validateRequired(tripTime, 'tripTime'),
    ];
    return validations.every((v) => v);
  };

  const validateEventForm = (): boolean => {
    const validations = [
      validateRequired(eventTitle, 'eventTitle'),
      validateRequired(eventDescription, 'eventDescription'),
      validateRequired(eventLocation, 'eventLocation'),
      validateDate(eventStartTime, 'eventStartTime'),
      validateDate(eventEndTime, 'eventEndTime'),
    ];
    if (new Date(eventEndTime) <= new Date(eventStartTime)) {
      setErrors((prev) => ({ ...prev, eventEndTime: 'End time must be after start time' }));
      return false;
    }
    return validations.every((v) => v);
  };

  const validateMarketForm = (): boolean => {
    return (
      validateRequired(productTitle, 'productTitle') &&
      validateRequired(productDescription, 'productDescription') &&
      validateNumber(productAmount, 'productAmount')
    );
  };

  const validateTaskForm = (): boolean => {
    const validations = [
      validateRequired(taskTitle, 'taskTitle'),
      validateRequired(taskDescription, 'taskDescription'),
      validateNumber(taskAmount, 'taskAmount'),
      validateDate(taskStart, 'taskStart'),
      validateDate(taskEnd, 'taskEnd'),
      validateRequired(taskType, 'taskType'),
    ];
    if (new Date(taskEnd) <= new Date(taskStart)) {
      setErrors((prev) => ({ ...prev, taskEnd: 'End date must be after start date' }));
      return false;
    }
    return validations.every((v) => v);
  };

  const validatePollForm = (): boolean => {
    setErrors((prev) => ({ ...prev, pollChoices: '' }));
    if (!validateRequired(pollQuestion, 'pollQuestion')) return false;
    const validChoices = pollChoices.filter((choice) => choice.trim() !== '');
    if (validChoices.length < 2) {
      setErrors((prev) => ({
        ...prev,
        pollChoices: 'At least two non-empty choices are required',
      }));
      return false;
    }
    const uniqueChoices = new Set(validChoices.map((c) => c.trim().toLowerCase()));
    if (uniqueChoices.size !== validChoices.length) {
      setErrors((prev) => ({ ...prev, pollChoices: 'Duplicate choices are not allowed' }));
      return false;
    }
    return true;
  };

  const validateRentForm = (): boolean => {
    return (
      validateRequired(apartmentTitle, 'apartmentTitle') &&
      validateRequired(apartmentDescription, 'apartmentDescription') &&
      validateNumber(apartmentRent, 'apartmentRent') &&
      validateRequired(apartmentAddress, 'apartmentAddress')
    );
  };

  const handleFileUpload = async (
    files: FileList | null,
    type: 'image' | 'video',
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
  ) => {
    if (files) {
      const fileArray = Array.from(files);
      const validFiles = fileArray.filter((file) =>
        type === 'image' ? file.type.startsWith('image/') : file.type.startsWith('video/'),
      );
      const processed =
        type === 'image'
          ? await Promise.all(validFiles.map((f) => compressImage(f)))
          : validFiles;
      setFiles((prev) => [...prev, ...processed]);
    }
  };

  const removeFile = (index: number, setFiles: React.Dispatch<React.SetStateAction<File[]>>) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const addPollChoice = () => {
    if (newChoice.trim()) {
      setPollChoices((prev) => [...prev, newChoice.trim()]);
      setNewChoice('');
    } else if (pollChoices.length < 6) {
      setPollChoices((prev) => [...prev, '']);
    }
  };

  const removePollChoice = (index: number) => {
    setPollChoices((prev) => prev.filter((_, i) => i !== index));
  };

  const updatePollChoice = (index: number, value: string) => {
    const newChoices = [...pollChoices];
    newChoices[index] = value;
    setPollChoices(newChoices);
  };

  const handleRecord = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      tinyAlert('error', 'Your browser does not support audio recording.');
      return;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.ondataavailable = (event) => {
      audioChunksRef.current.push(event.data);
    };
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
      setAudio(audioBlob);
      audioChunksRef.current = [];
    };
    if (!isPaused) {
      setIsRecording(true);
      setRecordingTime(0);
      startTimeRef.current = Date.now();
    }
    mediaRecorder.start();
    setIsPaused(false);
    timerRef.current = setInterval(() => {
      setRecordingTime(Math.floor((Date.now() - startTimeRef.current!) / 1000));
    }, 1000);
  };

  const handleStop = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.currentTime = 0;
      }
    }
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const handlePauseResume = () => {
    if (isRecording) {
      if (!isPaused) {
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.pause();
          setIsPaused(true);
          if (timerRef.current) clearInterval(timerRef.current);
          if (audioPlayerRef.current) {
            audioPlayerRef.current.pause();
            audioPlayerRef.current.currentTime = 0;
          }
        }
      } else {
        if (mediaRecorderRef.current) {
          mediaRecorderRef.current.resume();
          setIsPaused(false);
          startTimeRef.current = Date.now() - recordingTime * 1000;
          timerRef.current = setInterval(() => {
            setRecordingTime(Math.floor((Date.now() - startTimeRef.current!) / 1000));
          }, 1000);
        }
      }
    }
  };

  const handleDiscard = () => {
    setAudioURL(null);
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
    sessionStorage.removeItem('audioURL');
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const createImageII = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      if (e.target && typeof e.target.result === 'string') {
        setImages((prevImages) => [...prevImages, e.target?.result as string]);
      }
    };
    reader.readAsDataURL(file);
  };

  const deleteImage = (index: number) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    if (multipleFile) {
      const newFiles = [...multipleFile];
      newFiles.splice(index, 1);
      setMultipleFile(newFiles);
    }
  };

  const handleFileChange = async (value: FileList | null) => {
    if (value !== null && value?.length > 0) {
      const filesArray = Array.from(value);
      const compressedFiles = await Promise.all(filesArray.map((f) => compressImage(f)));
      setMultipleFile(compressedFiles);
      compressedFiles.forEach((file) => createImageII(file));
    }
  };

  const handleContinue = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setPostType('tweet');
    setHashtag('#tweet');
    if (openModal) {
      document.body.classList.remove('overflow-hidden');
    } else {
      document.body.classList.add('overflow-hidden');
    }
    if (textContent.length === 0) {
      setErrmessage('Kindly write something to continue');
    } else {
      setErrmessage('');
    }
    if (textContent.length > 0) {
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    document.body.classList.remove('overflow-hidden');
    setOpenModal(!openModal);
  };

  const handleCheckboxChange = (value: string) => {
    setSelectedCommunities((prevSelected) => {
      if (prevSelected.includes(value)) {
        return prevSelected.filter((item) => item !== value);
      } else {
        return [...prevSelected, value];
      }
    });
  };

  const handleCreateNewPost = async () => {
    setErrmessageII('');
    if (selectedCommunities.length === 0) {
      setErrmessageII('Select at least one community');
      return;
    }
    setLoading(true);
    setUploadProgress(0);

    let simulatedProgress = 0;
    const ticker = setInterval(() => {
      simulatedProgress += Math.random() * 8;
      if (simulatedProgress < 85) {
        setUploadProgress(Math.round(simulatedProgress));
      }
    }, 300);

    let data: Record<string, any> = {
      selected_community: selectedCommunities,
      post_type: postType,
    };

    switch (postType) {
      case 'job':
        data = {
          ...data,
          images: jobImages,
          videos: jobVideos,
          title: jobTitle,
          text_content: jobDescription,
          location: jobLocation,
          amount: salary,
          frequency: salaryFrequency,
          start_date: startDate,
          deadline_date: deadline,
        };
        break;
      case 'rides':
        data = {
          ...data,
          ride_user_type: userType,
          location: pickupLocation,
          destination: tripDestination,
          amount: offeredAmount,
          start_date: tripDate,
          trip_time: tripTime,
        };
        break;
      case 'events':
        data = {
          ...data,
          images: eventImages,
          videos: eventVideos,
          title: eventTitle,
          text_content: eventDescription,
          location: eventLocation,
          start_time: eventStartTime,
          deadline_date: eventEndTime,
        };
        break;
      case 'market':
        data = {
          ...data,
          images: productImages,
          title: productTitle,
          text_content: productDescription,
          amount: productAmount,
        };
        break;
      case 'task':
        data = {
          ...data,
          images: taskImages,
          title: taskTitle,
          text_content: taskDescription,
          amount: taskAmount,
          start_date: taskStart,
          deadline_date: taskEnd,
          task_type: taskType,
        };
        break;
      case 'poll':
        data = {
          ...data,
          title: pollQuestion,
          text_content: pollDescription,
          poll_options: pollChoices.filter((c) => c.trim() !== ''),
        };
        break;
      case 'rent':
        data = {
          ...data,
          images: apartmentImages,
          videos: apartmentVideos,
          title: apartmentTitle,
          text_content: apartmentDescription,
          amount: apartmentRent,
          address: apartmentAddress,
        };
        break;
      case 'tweet':
      default:
        data = {
          ...data,
          text_content: hashtag + ' ' + textContent,
          images: multipleFile,
          audio: audioBlob,
        };
        break;
    }

    console.log(data);

    try {
      await feeds_api.CreateNewFeed(data, (progress: number) => {
        clearInterval(ticker);
        setUploadProgress(progress);
      });

      clearInterval(ticker);
      setUploadProgress(100);

      setTimeout(() => {
        navigate(SITELINK.HOME);
      }, 600);
    } catch (error) {
      clearInterval(ticker);
      setUploadProgress(0);
      console.log(error);
      tinyAlert('error', 'An error occurred!');
    } finally {
      document.body.classList.remove('overflow-hidden');
      setLoading(false);
    }
  };

  return (
    <section className="bg-[#fff] dark:bg-neutral-900 h-[100vh] flex flex-col">
      <div className="fixed top-0 w-full z-50">
        <TopBar title="Create Post" link={SITELINK.HOME} />
      </div>

      <div className="mt-[48px] mb-4 flex-1 overflow-y-auto">
        {/* Profile display area */}
        <div className="flex gap-2 bg-white dark:bg-neutral-800 p-2 py-4 pb-2">
          <Link to={`${SITELINK.MY_PROFILE}/${user?.username}`} className="w-[50px] block">
            {user.profile_picture_url ? (
              <div className="flex-grow flex flex-col items-center">
                <div className="overflow-hidden flex justify-center">
                  <img
                    alt="profile"
                    src={user?.profile_picture_url}
                    className="object-cover rounded-full w-[40px] h-[40px] bg-gray-200 dark:bg-neutral-700 shadow-sm overflow-hidden"
                  />
                </div>
              </div>
            ) : (
              <span className="block w-[40px] h-[40px] bg-[#223f64] dark:bg-[#1a2f4a] rounded-full border border-green-100">
                <h3 className="text-white text-[1.4rem] uppercase font-[600] relative top-0.5 left-2.5">
                  {getFirstLetters(user?.first_name ?? user?.username)}
                </h3>
              </span>
            )}
          </Link>
          <div className="text-sm">
            <div>
              <span className="font-medium madimi-one text-lg dark:text-white">{user?.username}</span>
              {user?.is_verified && (
                <span>
                  <img src={Verify} alt="" className="h-[12px]" />
                </span>
              )}
            </div>
            <span className="block text-[10px] text-gray-600 dark:text-gray-400">{user?.email}</span>
          </div>
        </div>

        {/* Input area */}
        <div className="composer-area px-3 mt-6">
          <textarea
            value={textContent}
            placeholder="Have something to say?"
            className="text-sm font-[500] p-3 w-full border border-gray-300 dark:border-neutral-700 shadow-sm rounded-lg madimi-one dark:bg-neutral-800 dark:text-white dark:placeholder-gray-400"
            rows={5}
            onChange={(e) => setTextContent(e.target.value)}
          />

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              marginTop: '10px',
              marginBottom: '10px',
            }}
          >
            {images.map((imageSrc, index) => (
              <div key={index} style={{ position: 'relative', width: '100px', height: '100px' }}>
                <img
                  src={imageSrc}
                  alt={`Uploaded ${index}`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '5px',
                  }}
                />
                <span
                  onClick={() => deleteImage(index)}
                  style={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    backgroundColor: '#bbb4b4',
                    color: 'red',
                    borderRadius: '50%',
                    cursor: 'pointer',
                    padding: '3px',
                  }}
                >
                  <LuX />
                </span>
              </div>
            ))}
          </div>

          {audioURL && (
            <audio controls src={audioURL} className="bg-white dark:bg-neutral-800 w-full custom-audio-player mb-1" />
          )}
        </div>

        {/* Upload attachment area */}
        <div className="flex justify-between px-5 mt-5">
          <label
            htmlFor="dropzone-file"
            className="text-sm flex py-2 px-4 rounded-full shadow-sm gap-1 cursor-pointer border dark:border-neutral-700"
          >
            <LuImage className="text-base text-custom-600" />
            <span className="text-custom-600 font-medium text-xs">Add image</span>
            <input
              id="dropzone-file"
              ref={fileInputRef}
              type="file"
              accept=".png, .jpeg, .jpg"
              multiple
              onChange={(e) => handleFileChange(e.currentTarget.files)}
              className="hidden"
            />
          </label>

          <div>
            <div className="controls flex items-center gap-3">
              {!isRecording && !audioURL && (
                <button
                  onClick={handleRecord}
                  className="text-sm flex py-2 px-4 rounded-full shadow-sm gap-1 cursor-pointer border dark:border-neutral-700"
                  title="Start Recording"
                  disabled={isRecording}
                >
                  <LuMic className="text-base text-custom-600" />
                  <span className="text-custom-600 font-medium text-xs">Voice Note</span>
                </button>
              )}
            </div>
            <div className="recording-status flex w-[50%] md:w-full items-center gap-4">
              {(isRecording || audioURL) && (
                <button onClick={handleDiscard} title="Discard Recording" className="mt-[5px]">
                  <LuTrash2 className="text-lg text-red-700 font-bold" strokeWidth={1} />
                </button>
              )}
              {isRecording && (
                <span className="text-custom-600 font-medium">
                  {new Date(recordingTime * 1000).toISOString().substr(14, 5)}
                </span>
              )}
              {isRecording && (
                <button
                  onClick={handlePauseResume}
                  disabled={!isRecording}
                  title={isPaused ? 'Resume' : 'Pause'}
                >
                  {isPaused ? (
                    <LuMic className="text-custom-600 font-medium" strokeWidth={1} />
                  ) : (
                    <LuPause className="text-custom-600 font-medium" strokeWidth={1} />
                  )}
                </button>
              )}
              {isRecording && (
                <button onClick={handleStop} disabled={!isRecording} title="Stop Recording">
                  <LuPlay className="text-custom-600 font-medium" strokeWidth={1} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Hash-tags section */}
        <div className="px-5">
          <div className="pt-2 mt-2 flex gap-2 flex-wrap flex-row">
            {['job', 'rides', 'events', 'market', 'rent', 'task', 'poll'].map(
              (item, index: number) => (
                <button
                  key={index}
                  type="button"
                  className={`text-xs flex py-2 gap-1 rounded-full px-4 border font-semibold whitespace-nowrap text-custom-600 capitalize ${
                    hashtag === `#${item}` ? 'bg-blue-700 text-white' : 'bg-gray-100 dark:bg-neutral-700'
                  } dark:border-neutral-600`}
                  onClick={() => {
                    setHashtag(`#${item}`);
                    setShowTypeModal(true);
                  }}
                >
                  {item}
                </button>
              ),
            )}
          </div>
        </div>

        <div className="w-full aspect-square flex items-center justify-center mt-5">
          <img src={ComposerBanner} alt="" className="object-cover w-full h-full" />
        </div>
      </div>

      {/* Fixed Continue Button at Bottom */}
      <div className="px-5 pb-4 bg-white dark:bg-neutral-800 border-t dark:border-neutral-700 pt-4 sticky bottom-0">
        <p className="text-red-600 text-xs text-center">
          {textContent.length === 0 && errmessage}
        </p>
        <button
          type="button"
          onClick={handleContinue}
          className="w-full text-sm flex gap-1 mt-3 justify-center bg-custom-600 text-white rounded-xl px-5 border dark:border-custom-600 font-medium py-3 mb-4"
        >
          Continue
        </button>
      </div>

      {/* Communities selection modal */}
      <Modal isOpen={openModal} onClose={handleCloseModal}>
        <section className="w-[90vw]">
          <div className="px-2 border-gray-400 dark:border-neutral-700 rounded-lg mb-3">
            <div className="p-2 mb-3 text-medium rounded-t-lg">
              <label className="border-b dark:border-neutral-700 font-semibold text-lg w-full p-2 flex dark:text-white">
                Select communities
              </label>
              <div className="overflow-y-scroll h-[200px] px-1 text-xs font-medium dark:text-white">
                {userCommunitiesOptions.map((item: any, index: number) => (
                  <div
                    key={index}
                    className="first:border-t-0 border-t dark:border-neutral-700 p-1 py-0 flex items-center gap-2"
                  >
                    <input
                      type="checkbox"
                      id={`community_${index}`}
                      name="community"
                      value={item.value}
                      onChange={() => handleCheckboxChange(item.value)}
                      checked={selectedCommunities.includes(item.value)}
                    />
                    <label className="pl-2" htmlFor={`community_${index}`}>
                      {truncateText(item.label, 40)}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-red-600 text-xs text-center">
              {selectedCommunities.length === 0 && errmessageII}
            </p>

            <button
              type="button"
              onClick={handleCreateNewPost}
              disabled={loading}
              className="text-sm flex py-3 gap-1 justify-center bg-custom-600 dark:border-custom-600 text-white rounded-lg px-5 border font-medium w-full"
            >
              {loading ? <LoadingPercentage percentage={uploadProgress} /> : 'Post'}
            </button>
          </div>
        </section>
      </Modal>

      {/* Jobs modal */}
      <Modal
        isOpen={showTypeModal && hashtag === '#job'}
        onClose={() => handleDismissTypeModal('#job')}
        roundness="rounded-t-3xl"
        alignment="items-end"
      >
        <section className="p-4 w-[100vw] h-[90vh] flex flex-col bg-white dark:bg-neutral-800">
          <div className="flex justify-between items-center pb-4 border-b dark:border-neutral-700">
            <button onClick={() => handleDismissTypeModal('#job')}>
              <LuX className="size-5 dark:text-white" />
            </button>
            <span className="font-bold dark:text-white">Post a Job</span>
            <LuPlus className="size-5 dark:text-white" />
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border placeholder:text-sm ${
                  errors.jobTitle ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Title"
                value={jobTitle}
                onChange={(e) => {
                  setJobTitle(e.target.value);
                  validateRequired(e.target.value, 'jobTitle');
                }}
              />
              {errors.jobTitle && <p className="text-red-500 text-xs mt-1">{errors.jobTitle}</p>}
            </div>
            <div>
              <textarea
                className={`w-full border p-3 h-40 resize-none rounded-lg text-sm placeholder:text-sm ${
                  errors.jobDescription ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Description"
                value={jobDescription}
                onChange={(e) => {
                  setJobDescription(e.target.value);
                  validateRequired(e.target.value, 'jobDescription');
                }}
              />
              {errors.jobDescription && (
                <p className="text-red-500 text-xs mt-1">{errors.jobDescription}</p>
              )}
            </div>
            <div className="flex gap-4 justify-between items-center">
              <label className="w-fit">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files, 'image', setJobImages)}
                />
                <div className="border dark:border-neutral-600 py-2 px-4 rounded-full flex items-center justify-center gap-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700 dark:text-white">
                  <LuImage className="size-5" /> Add Image
                </div>
              </label>
              <label className="w-fit">
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files, 'video', setJobVideos)}
                />
                <div className="border dark:border-neutral-600 py-2 px-4 rounded-full flex items-center justify-center gap-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700 dark:text-white">
                  <LuVideo className="size-5" /> Add Video
                </div>
              </label>
            </div>
            {jobImages.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {jobImages.map((file, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt=""
                      className="w-20 h-20 object-cover rounded"
                    />
                    <button
                      onClick={() => removeFile(index, setJobImages)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6"
                    >
                      <LuX className="mx-auto" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm placeholder:text-sm border ${
                  errors.jobLocation ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Location"
                value={jobLocation}
                onChange={(e) => {
                  setJobLocation(e.target.value);
                  validateRequired(e.target.value, 'jobLocation');
                }}
              />
              {errors.jobLocation && (
                <p className="text-red-500 text-xs mt-1">{errors.jobLocation}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.salary ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Enter Amount"
                value={salary}
                onChange={(e) => {
                  setSalary(e.target.value);
                  validateNumber(e.target.value, 'salary');
                }}
              />
              {errors.salary && <p className="text-red-500 text-xs mt-1">{errors.salary}</p>}
            </div>
            <div>
              <CustomDropdown
                options={salaryFrequencyOptions}
                value={salaryFrequency}
                onChange={(value) => {
                  setSalaryFrequency(value);
                  validateRequired(value, 'salaryFrequency');
                }}
                placeholder="Select Frequency"
                error={errors.salaryFrequency}
              />
            </div>
            <CustomDateInput
              placeholder="Start Date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                validateDate(e.target.value, 'startDate');
              }}
              error={errors.startDate}
            />
            <CustomDateInput
              placeholder="Deadline Date"
              value={deadline}
              onChange={(e) => {
                setDeadline(e.target.value);
                validateDate(e.target.value, 'deadline');
              }}
              error={errors.deadline}
            />
          </div>
          <div className="pt-4 border-t dark:border-neutral-700">
            <button
              onClick={() => {
                if (validateJobForm()) openCommunitiesModal('job');
              }}
              className="py-3 w-full rounded-lg bg-custom-600 text-white hover:bg-custom-700 transition-colors text-sm"
            >
              Continue
            </button>
          </div>
        </section>
      </Modal>

      {/* Rides modal */}
      <Modal
        isOpen={showTypeModal && hashtag === '#rides'}
        onClose={() => handleDismissTypeModal('#rides')}
        roundness="rounded-t-3xl"
        alignment="items-end"
      >
        <section className="p-4 w-[100vw] h-[90vh] flex flex-col bg-white dark:bg-neutral-800">
          <div className="flex justify-between items-center pb-4 border-b dark:border-neutral-700">
            <button onClick={() => handleDismissTypeModal('#rides')}>
              <LuX className="size-5 dark:text-white" />
            </button>
            <span className="font-bold dark:text-white">Catapot Rides</span>
            <LuPlus className="size-5 dark:text-white" />
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            <CustomDropdown
              options={userTypeOptions}
              value={userType}
              onChange={(value) => {
                setUserType(value);
                validateRequired(value, 'userType');
              }}
              placeholder="Select User Type"
              error={errors.userType}
            />
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.pickupLocation ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Pickup Location"
                value={pickupLocation}
                onChange={(e) => {
                  setPickupLocation(e.target.value);
                  validateRequired(e.target.value, 'pickupLocation');
                }}
              />
              {errors.pickupLocation && (
                <p className="text-red-500 text-xs mt-1">{errors.pickupLocation}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.tripDestination ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Destination"
                value={tripDestination}
                onChange={(e) => {
                  setTripDestination(e.target.value);
                  validateRequired(e.target.value, 'tripDestination');
                }}
              />
              {errors.tripDestination && (
                <p className="text-red-500 text-xs mt-1">{errors.tripDestination}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.offeredAmount ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Offered Amount"
                value={offeredAmount}
                onChange={(e) => {
                  setOfferedAmount(e.target.value);
                  validateNumber(e.target.value, 'offeredAmount');
                }}
              />
              {errors.offeredAmount && (
                <p className="text-red-500 text-xs mt-1">{errors.offeredAmount}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <CustomDateInput
                placeholder="Date"
                value={tripDate}
                onChange={(e) => {
                  setTripDate(e.target.value);
                  validateDate(e.target.value, 'tripDate');
                }}
                error={errors.tripDate}
              />
              <CustomTimeInput
                placeholder="Time"
                value={tripTime}
                onChange={(e) => {
                  setTripTime(e.target.value);
                  validateRequired(e.target.value, 'tripTime');
                }}
                error={errors.tripTime}
              />
            </div>
          </div>
          <div className="pt-4 border-t dark:border-neutral-700">
            <button
              onClick={() => {
                if (validateRideForm()) openCommunitiesModal('rides');
              }}
              className="py-3 w-full rounded-lg text-sm bg-custom-600 text-white hover:bg-custom-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </section>
      </Modal>

      {/* Events modal */}
      <Modal
        isOpen={showTypeModal && hashtag === '#events'}
        onClose={() => handleDismissTypeModal('#events')}
        roundness="rounded-t-3xl"
        alignment="items-end"
      >
        <section className="p-4 w-[100vw] h-[90vh] flex flex-col bg-white dark:bg-neutral-800">
          <div className="flex justify-between items-center pb-4 border-b dark:border-neutral-700">
            <button onClick={() => handleDismissTypeModal('#events')}>
              <LuX className="size-5 dark:text-white" />
            </button>
            <span className="font-bold dark:text-white">Create an Event</span>
            <LuPlus className="size-5 dark:text-white" />
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border placeholder:text-sm ${
                  errors.eventTitle ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Title"
                value={eventTitle}
                onChange={(e) => {
                  setEventTitle(e.target.value);
                  validateRequired(e.target.value, 'eventTitle');
                }}
              />
              {errors.eventTitle && (
                <p className="text-red-500 text-xs mt-1">{errors.eventTitle}</p>
              )}
            </div>
            <div>
              <textarea
                className={`w-full border p-3 h-40 resize-none rounded-lg text-sm placeholder:text-sm ${
                  errors.eventDescription ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Description"
                value={eventDescription}
                onChange={(e) => {
                  setEventDescription(e.target.value);
                  validateRequired(e.target.value, 'eventDescription');
                }}
              />
              {errors.eventDescription && (
                <p className="text-red-500 text-xs mt-1">{errors.eventDescription}</p>
              )}
            </div>
            <div className="flex gap-4 justify-between items-center">
              <label className="w-fit">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files, 'image', setEventImages)}
                />
                <div className="border dark:border-neutral-600 py-2 px-4 text-xs rounded-full flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700 dark:text-white">
                  <LuImage className="size-5" /> Add Image
                </div>
              </label>
              <label className="w-fit">
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files, 'video', setEventVideos)}
                />
                <div className="border dark:border-neutral-600 py-2 px-4 rounded-full flex items-center justify-center gap-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700 dark:text-white">
                  <LuVideo className="size-5" /> Add Video
                </div>
              </label>
            </div>
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.eventLocation ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Location"
                value={eventLocation}
                onChange={(e) => {
                  setEventLocation(e.target.value);
                  validateRequired(e.target.value, 'eventLocation');
                }}
              />
              {errors.eventLocation && (
                <p className="text-red-500 text-xs mt-1">{errors.eventLocation}</p>
              )}
            </div>
            <CustomDateTimeInput
              placeholder="Start Date and Time"
              value={eventStartTime}
              onChange={(e) => {
                setEventStartTime(e.target.value);
                validateDate(e.target.value, 'eventStartTime');
              }}
              error={errors.eventStartTime}
            />
            <CustomDateTimeInput
              placeholder="End Date and Time"
              value={eventEndTime}
              onChange={(e) => {
                setEventEndTime(e.target.value);
                validateDate(e.target.value, 'eventEndTime');
              }}
              error={errors.eventEndTime}
            />
          </div>
          <div className="pt-4 border-t dark:border-neutral-700">
            <button
              onClick={() => {
                if (validateEventForm()) openCommunitiesModal('events');
              }}
              className="py-3 w-full rounded-lg bg-custom-600 text-white hover:bg-custom-700 transition-colors text-sm"
            >
              Continue
            </button>
          </div>
        </section>
      </Modal>

      {/* Market modal */}
      <Modal
        isOpen={showTypeModal && hashtag === '#market'}
        onClose={() => handleDismissTypeModal('#market')}
        roundness="rounded-t-3xl"
        alignment="items-end"
      >
        <section className="p-4 w-[100vw] h-[90vh] flex flex-col bg-white dark:bg-neutral-800">
          <div className="flex justify-between items-center pb-4 border-b dark:border-neutral-700">
            <button onClick={() => handleDismissTypeModal('#market')}>
              <LuX className="size-5 dark:text-white" />
            </button>
            <span className="font-bold dark:text-white">Sell a Product</span>
            <LuPlus className="size-5 dark:text-white" />
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.productTitle ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Title"
                value={productTitle}
                onChange={(e) => {
                  setProductTitle(e.target.value);
                  validateRequired(e.target.value, 'productTitle');
                }}
              />
              {errors.productTitle && (
                <p className="text-red-500 text-xs mt-1">{errors.productTitle}</p>
              )}
            </div>
            <div>
              <textarea
                className={`w-full border p-3 h-40 resize-none rounded-lg text-sm placeholder:text-sm ${
                  errors.productDescription ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Description"
                value={productDescription}
                onChange={(e) => {
                  setProductDescription(e.target.value);
                  validateRequired(e.target.value, 'productDescription');
                }}
              />
              {errors.productDescription && (
                <p className="text-red-500 text-xs mt-1">{errors.productDescription}</p>
              )}
            </div>
            <div>
              <label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files, 'image', setProductImages)}
                />
                <div className="border dark:border-neutral-600 py-2 px-4 rounded-full flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700 w-fit text-xs dark:text-white">
                  <LuImage className="size-5" /> Add Image
                </div>
              </label>
              {productImages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {productImages.map((file, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt=""
                        className="w-20 h-20 object-cover rounded"
                      />
                      <button
                        onClick={() => removeFile(index, setProductImages)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6"
                      >
                        <LuX className="mx-auto" size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.productAmount ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Price"
                value={productAmount}
                onChange={(e) => {
                  setProductAmount(e.target.value);
                  validateNumber(e.target.value, 'productAmount');
                }}
              />
              {errors.productAmount && (
                <p className="text-red-500 text-xs mt-1">{errors.productAmount}</p>
              )}
            </div>
          </div>
          <div className="pt-4 border-t dark:border-neutral-700">
            <button
              onClick={() => {
                if (validateMarketForm()) openCommunitiesModal('market');
              }}
              className="py-3 w-full rounded-lg bg-custom-600 text-white hover:bg-custom-700 transition-colors text-sm"
            >
              Continue
            </button>
          </div>
        </section>
      </Modal>

      {/* Task modal */}
      <Modal
        isOpen={showTypeModal && hashtag === '#task'}
        onClose={() => handleDismissTypeModal('#task')}
        roundness="rounded-t-3xl"
        alignment="items-end"
      >
        <section className="p-4 w-[100vw] h-[90vh] flex flex-col bg-white dark:bg-neutral-800">
          <div className="flex justify-between items-center pb-4 border-b dark:border-neutral-700">
            <button onClick={() => handleDismissTypeModal('#task')}>
              <LuX className="size-5 dark:text-white" />
            </button>
            <span className="font-bold dark:text-white">Create Task</span>
            <LuPlus className="size-5 dark:text-white" />
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.taskTitle ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Title"
                value={taskTitle}
                onChange={(e) => {
                  setTaskTitle(e.target.value);
                  validateRequired(e.target.value, 'taskTitle');
                }}
              />
              {errors.taskTitle && <p className="text-red-500 text-xs mt-1">{errors.taskTitle}</p>}
            </div>
            <div>
              <textarea
                className={`w-full border p-3 text-sm placeholder:text-sm h-40 resize-none rounded-lg ${
                  errors.taskDescription ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Description"
                value={taskDescription}
                onChange={(e) => {
                  setTaskDescription(e.target.value);
                  validateRequired(e.target.value, 'taskDescription');
                }}
              />
              {errors.taskDescription && (
                <p className="text-red-500 text-xs mt-1">{errors.taskDescription}</p>
              )}
            </div>
            <div>
              <label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files, 'image', setTaskImages)}
                />
                <div className="border dark:border-neutral-600 py-2 px-4 rounded-full flex items-center justify-center gap-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700 w-fit dark:text-white">
                  <LuImage className="size-5" /> Add Image
                </div>
              </label>
            </div>
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.taskAmount ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Enter Amount"
                value={taskAmount}
                onChange={(e) => {
                  setTaskAmount(e.target.value);
                  validateNumber(e.target.value, 'taskAmount');
                }}
              />
              {errors.taskAmount && (
                <p className="text-red-500 text-xs mt-1">{errors.taskAmount}</p>
              )}
            </div>
            <CustomDateInput
              placeholder="Starting Date"
              value={taskStart}
              onChange={(e) => {
                setTaskStart(e.target.value);
                validateDate(e.target.value, 'taskStart');
              }}
              error={errors.taskStart}
            />
            <CustomDateInput
              placeholder="Deadline Date"
              value={taskEnd}
              onChange={(e) => {
                setTaskEnd(e.target.value);
                validateDate(e.target.value, 'taskEnd');
              }}
              error={errors.taskEnd}
            />
            <CustomDropdown
              options={taskTypeOptions}
              value={taskType}
              onChange={(value) => {
                setTaskType(value);
                validateRequired(value, 'taskType');
              }}
              placeholder="Select Task Type"
              error={errors.taskType}
            />
          </div>
          <div className="pt-4 border-t dark:border-neutral-700">
            <button
              onClick={() => {
                if (validateTaskForm()) openCommunitiesModal('task');
              }}
              className="py-3 w-full rounded-lg bg-custom-600 text-white hover:bg-custom-700 transition-colors text-sm"
            >
              Continue
            </button>
          </div>
        </section>
      </Modal>

      {/* Poll modal */}
      <Modal
        isOpen={showTypeModal && hashtag === '#poll'}
        onClose={() => handleDismissTypeModal('#poll')}
        roundness="rounded-t-3xl"
        alignment="items-end"
      >
        <section className="p-4 w-[100vw] h-[90vh] flex flex-col bg-white dark:bg-neutral-800">
          <div className="flex justify-between items-center pb-4 border-b dark:border-neutral-700">
            <button onClick={() => handleDismissTypeModal('#poll')}>
              <LuX className="size-5 dark:text-white" />
            </button>
            <span className="font-bold dark:text-white">Create Poll</span>
            <LuPlus className="size-5 dark:text-white" />
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.pollQuestion ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Question"
                value={pollQuestion}
                onChange={(e) => {
                  setPollQuestion(e.target.value);
                  validateRequired(e.target.value, 'pollQuestion');
                }}
                onBlur={(e) => validateRequired(e.target.value, 'pollQuestion')}
              />
              {errors.pollQuestion && (
                <p className="text-red-500 text-xs mt-1">{errors.pollQuestion}</p>
              )}
            </div>
            <div>
              <textarea
                className={`w-full border p-3 text-sm placeholder:text-sm h-32 resize-none rounded-lg ${
                  errors.pollDescription ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Poll Description (Optional)"
                value={pollDescription}
                onChange={(e) => {
                  setPollDescription(e.target.value);
                  setErrors((prev) => ({ ...prev, pollDescription: '' }));
                }}
              />
              {errors.pollDescription && (
                <p className="text-red-500 text-xs mt-1">{errors.pollDescription}</p>
              )}
            </div>
            <div>
              <div className="space-y-3">
                <label className="font-medium text-sm dark:text-white">Options</label>
                {pollChoices.map((choice, index) => (
                  <div key={index} className="flex items-center gap-3 border-b dark:border-neutral-700">
                    {index < 2 ? (
                      <div className="w-5 h-5 flex items-center justify-center">
                        <span className="text-gray-400 dark:text-gray-500 text-sm">{index + 1}.</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removePollChoice(index)}
                        className="w-5 h-5 flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-red-500"
                      >
                        <LuX size={16} />
                      </button>
                    )}
                    <input
                      type="text"
                      className="flex-1 py-3 text-sm dark:bg-neutral-700 dark:text-white dark:placeholder-gray-400"
                      placeholder={`Choice ${index + 1}`}
                      value={choice}
                      onChange={(e) => updatePollChoice(index, e.target.value)}
                      onBlur={(e) => updatePollChoice(index, e.target.value.trim())}
                    />
                  </div>
                ))}
                <div className="flex items-center gap-3 border-b dark:border-neutral-700">
                  <button
                    type="button"
                    onClick={addPollChoice}
                    className="w-5 h-5 flex items-center justify-center text-custom-600 hover:text-custom-700"
                  >
                    <LuPlus size={18} />
                  </button>
                  <input
                    type="text"
                    className="flex-1 py-3 text-sm dark:bg-neutral-700 dark:text-white dark:placeholder-gray-400"
                    placeholder={`Choice ${pollChoices.length + 1}`}
                    value={newChoice}
                    onChange={(e) => setNewChoice(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPollChoice()}
                    onBlur={() => {
                      if (newChoice.trim()) addPollChoice();
                    }}
                  />
                  {newChoice.trim() && (
                    <button
                      type="button"
                      onClick={addPollChoice}
                      className="px-4 py-2 text-sm font-medium text-custom-600 hover:text-custom-700"
                    >
                      Add
                    </button>
                  )}
                </div>
                {errors.pollChoices && (
                  <p className="text-red-500 text-xs mt-1">{errors.pollChoices}</p>
                )}
              </div>
            </div>
            {pollChoices.length < 6 && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    if (newChoice.trim()) {
                      addPollChoice();
                    } else {
                      setPollChoices((prev) => [...prev, '']);
                    }
                  }}
                  className="text-sm text-custom-600 hover:text-custom-700 font-medium"
                >
                  + Add another option
                </button>
              </div>
            )}
          </div>
          <div className="pt-4 border-t dark:border-neutral-700">
            <button
              onClick={() => {
                if (validatePollForm()) openCommunitiesModal('poll');
              }}
              className="py-3 w-full text-sm rounded-lg bg-custom-600 text-white hover:bg-custom-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </section>
      </Modal>

      {/* Rent modal */}
      <Modal
        isOpen={showTypeModal && hashtag === '#rent'}
        onClose={() => handleDismissTypeModal('#rent')}
        roundness="rounded-t-3xl"
        alignment="items-end"
      >
        <section className="p-4 w-[100vw] h-[90vh] flex flex-col bg-white dark:bg-neutral-800">
          <div className="flex justify-between items-center pb-4 border-b dark:border-neutral-700">
            <button onClick={() => handleDismissTypeModal('#rent')}>
              <LuX className="size-5 dark:text-white" />
            </button>
            <span className="font-bold dark:text-white">Rent an Apartment</span>
            <LuPlus className="size-5 dark:text-white" />
          </div>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.apartmentTitle ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Title"
                value={apartmentTitle}
                onChange={(e) => {
                  setApartmentTitle(e.target.value);
                  validateRequired(e.target.value, 'apartmentTitle');
                }}
              />
              {errors.apartmentTitle && (
                <p className="text-red-500 text-xs mt-1">{errors.apartmentTitle}</p>
              )}
            </div>
            <div>
              <textarea
                className={`w-full border p-3 text-sm placeholder:text-sm h-40 resize-none rounded-lg ${
                  errors.apartmentDescription ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Description"
                value={apartmentDescription}
                onChange={(e) => {
                  setApartmentDescription(e.target.value);
                  validateRequired(e.target.value, 'apartmentDescription');
                }}
              />
              {errors.apartmentDescription && (
                <p className="text-red-500 text-xs mt-1">{errors.apartmentDescription}</p>
              )}
            </div>
            <div className="flex gap-4 items-center justify-between">
              <label className="w-fit">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files, 'image', setApartmentImages)}
                />
                <div className="border dark:border-neutral-600 py-2 px-4 rounded-full flex items-center justify-center gap-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700 dark:text-white">
                  <LuImage className="size-5" /> Add Image
                </div>
              </label>
              <label className="w-fit">
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e.target.files, 'video', setApartmentVideos)}
                />
                <div className="border dark:border-neutral-600 py-2 px-4 rounded-full flex items-center justify-center gap-2 text-xs cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-700 dark:text-white">
                  <LuVideo className="size-5" /> Add Video
                </div>
              </label>
            </div>
            {apartmentImages.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {apartmentImages.map((file, index) => (
                  <div key={index} className="relative">
                    <img
                      src={URL.createObjectURL(file)}
                      alt=""
                      className="w-20 h-20 object-cover rounded"
                    />
                    <button
                      onClick={() => removeFile(index, setApartmentImages)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6"
                    >
                      <LuX className="mx-auto" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.apartmentRent ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Enter Amount"
                value={apartmentRent}
                onChange={(e) => {
                  setApartmentRent(e.target.value);
                  validateNumber(e.target.value, 'apartmentRent');
                }}
              />
              {errors.apartmentRent && (
                <p className="text-red-500 text-xs mt-1">{errors.apartmentRent}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                className={`w-full rounded-md px-4 py-3 text-sm border ${
                  errors.apartmentAddress ? 'border-red-500' : 'focus:border-custom-500'
                } dark:bg-neutral-700 dark:border-neutral-600 dark:text-white dark:placeholder-gray-400`}
                placeholder="Enter Address"
                value={apartmentAddress}
                onChange={(e) => {
                  setApartmentAddress(e.target.value);
                  validateRequired(e.target.value, 'apartmentAddress');
                }}
              />
              {errors.apartmentAddress && (
                <p className="text-red-500 text-xs mt-1">{errors.apartmentAddress}</p>
              )}
            </div>
          </div>
          <div className="pt-4 border-t dark:border-neutral-700">
            <button
              onClick={() => {
                if (validateRentForm()) openCommunitiesModal('rent');
              }}
              className="py-3 w-full text-sm rounded-lg bg-custom-600 text-white hover:bg-custom-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </section>
      </Modal>
    </section>
  );
};

export default Composer;